import React, { useCallback, useMemo, useRef } from "react";
import { Column, DataGrid, Editing } from "devextreme-react/data-grid";
import UserCell from "../generic/UserCell";
import DateDisplay from "../generic/DateDisplay";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteResource,
  postResource,
  updateResource,
} from "../../redux/slices/resourceSlice";
import { handleNotificationMessage } from "../../utils/notifications";
import { fromCustomDate, toCustomDate } from "../../utils/date";
import useResources from "../../hooks/useResources";
import CustomSelection from "../generic/CustomSelection";
import { DateBox } from "devextreme-react";
import { getUserId } from "../../redux/slices/userSlice";

const columns = [
  { dataField: "id", allowEditing: false, caption: "Id", width: 50 },

  {
    dataField: "date",
    caption: "Date",
    type: "date",
  },
  { dataField: "note", caption: "Note" },
  { dataField: "description", caption: "Description" },
  { dataField: "amount", caption: "Amount", dataType: "number" },
  {
    dataField: "user_id",
    allowEditing: false,
    caption: "User",
    cellRender: (data) => <UserCell data={data} />,
  },
  {
    dataField: "trade_case_id",
    allowEditing: false,
    caption: "Trade case id",
  },
  {
    dataField: "invoice_id",
    allowEditing: false,
    caption: "Invoice id",
  },
];

const formatData = (data) => {
  return {
    ...data,
    date: data.date || toCustomDate(),
  };
};

const InvoicePaymentGrid = ({ id, resourceName, tradeCaseId }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => getUserId(state));

  const gridRef = useRef(null);
  const { invoicePayments } = useResources();

  const onValueChanged = useCallback((rowIndex, value, dataField) => {
    gridRef.current.instance.cellValue(rowIndex, dataField, value);
  }, []);

  const onInitNewRow = useCallback(
    ({ data }) => {
      //TODO : get name from token
      data.user_id = userId;
      data.invoice_id = id;
      data.trade_case_id = tradeCaseId;
    },
    [userId]
  );

  const onSaving = useCallback(
    (e) => {
      e.changes.forEach((change) => {
        switch (change.type) {
          case "insert":
            dispatch(
              postResource({
                path: `${resourceName}`,
                data: {
                  invoice_id: id,
                  ...formatData(change.data),
                },
              })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();

              const { path, error } = payload;
              const data = payload.data;
              if (!data) return;
              const { id } = data;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          case "update":
            dispatch(
              updateResource({
                path: resourceName,
                id: change.key.id,
                data: change.data,
              })
            ).then(({ payload }) => {
              if (e.cancel != null) {
                e.cancel = true;
                e.component.cancelEditData();
              }

              const { path, id, error } = payload;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          case "remove":
            dispatch(
              deleteResource({ path: resourceName, id: change.key.id })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();

              const { path, id, error } = payload;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          default:
            return;
        }
      });

      e.cancel = true;
    },
    [dispatch, id, resourceName]
  );

  return useMemo(
    () => (
      <DataGrid
        id="gridContainer"
        ref={gridRef}
        rowAlternationEnabled={true}
        dataSource={invoicePayments}
        allowColumnReordering={true}
        showBorders={true}
        onSaving={onSaving}
        onInitNewRow={onInitNewRow}
      >
        <Editing
          mode="batch"
          allowUpdating={true}
          allowAdding={true}
          allowDeleting={true}
          allowSorting={false}
        />

        {columns.map((column, idx) => {
          if (column.type === "customSelection")
            return (
              <Column
                key={column.dataField}
                dataField={column.dataField}
                caption={column.caption}
                width={column.width}
                editCellRender={(cell) => {
                  return (
                    <CustomSelection
                      value={cell.data[column.dataField]}
                      dataSource={column.dataSource}
                      handleChange={(e) =>
                        onValueChanged(cell.rowIndex, e.value, column.dataField)
                      }
                    />
                  );
                }}
              />
            );
          else if (column.type === "date")
            return (
              <Column
                key={column.dataField}
                dataField={column.dataField}
                caption={column.caption}
                width={column.width}
                cellRender={DateDisplay}
                editCellRender={(cell) => (
                  <DateBox
                    displayFormat="dd/MM/yyyy"
                    onValueChanged={(e) =>
                      onValueChanged(
                        cell.rowIndex,
                        toCustomDate(e.value),
                        column.dataField
                      )
                    }
                    value={fromCustomDate(cell.data[column.dataField])}
                  />
                )}
              />
            );
          else
            return (
              <Column
                key={column.dataField}
                cellRender={column.cellRender}
                dataField={column.dataField}
                allowEditing={column.allowEditing}
                caption={column.caption}
                dataType={column.dataType}
                width={column.width}
              />
            );
        })}
      </DataGrid>
    ),
    [invoicePayments, onInitNewRow, onSaving, onValueChanged]
  );
};

export default InvoicePaymentGrid;
